import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { useKeyboardListener } from '../../utils/hooks';
import { Box } from '../Box';
import './ButtonStyles.css';

const func = (e) => e;
const emptyObject = {};

// TODO move ButtonContainer to own file
// eslint-disable-next-line react/prop-types
const ButtonContainer = ({ href, children, newTab, buttonStyles, loading, disabled }) => {
  if (href && !loading && !disabled) {
    return (
      <a
        href={href}
        style={{ ...buttonStyles?.link }}
        target={newTab ? '_blank' : '_self'}
        rel='noreferrer'
      >
        {children}
      </a>
    );
  }

  return <>{children}</>;
};

/**
 * Clicky thing that does something when clicked
 *
 * <h2>When to use</h2>
 *
 *
 * <h3>Where it's used</h3>
 * * TODO figure out
 *
 * <h2>Usage</h2>
 *
 * <h3>Features</h3>
 *
 * <h3>Props</h3>
 *
 *
 * <h3>Examples</h3>
 *
 * ```<Button>Hello world</Box>```
 *
 */
export const Button = ({
  text,
  children = null,
  type,
  className = '',
  style = emptyObject,
  onClick = func,
  disabled = false,
  onEnter = func,
  resetFocus = false,
  fullWidth = false,
  loading = false,
  underline = false,
  href = null,
  newTab = true,
  color = null,
  centered = true,
  fullWidthOnMobile = false,
  forceHoverState = false,
  title = null,
  onHover = func,
  onLeave = func,
}) => {
  // TODO button should take in a THEME.
  // TODO look at how menu portal does this with material UI

  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  // TODO remove this in favour of styles
  const getClass = () => {
    switch (type) {
      case 'primary':
        return 'button-primary';
      case 'primaryRed':
        return 'button-primary-red';
      case 'secondary':
        return 'button-secondary';
      case 'tertiary':
        return 'button-tertiary';
      case 'standard':
        return 'button-standard';
      case 'box':
        return 'button-box';
      case 'text':
        return 'button-text';
      case 'table':
        return 'table-button';
      case 'icon':
        return 'button-icon';
      case 'custom':
        return '';
      default:
        return 'button-primary';
    }
  };

  const ref = useRef(null);

  const resetPageFocus = () => {
    if (resetFocus) {
      // TODO
    }
  };

  const handleClick = (event) => {
    if (disabled || loading) {
      return;
    }

    resetPageFocus();
    onClick(event, ref);
    event.stopPropagation();
  };

  const handleEnter = () => {
    if (disabled || loading) {
      return;
    }

    resetPageFocus();
    onEnter();
  };

  // Remove the hovered state if we're no longer forcing hover
  useEffect(() => {
    if (!forceHoverState) {
      setHovered(false);
    }
  }, [forceHoverState]);

  // const handleKeyPress = (event) => {
  //   // Return early if this is not the active element
  //   if (document.activeElement !== ref.current) {
  //     return;
  //   }
  //
  //   // Blur on escape
  //   if (event.key === 'Escape') {
  //     document.activeElement.blur();
  //   }
  //
  //   // If press enter, select the hovered item
  //   if (event.key === 'Enter') {
  //     handleEnter(event);
  //     handleClick(event);
  //   }
  // };
  //
  // // Add keyboard event listener to input
  // useEffect(() => {
  //   document.addEventListener('keydown', handleKeyPress);
  //   return () => document.removeEventListener('keydown', handleKeyPress);
  // });

  useKeyboardListener(ref, () => {}, [
    {
      key: 'Enter',
      action: (event) => {
        handleEnter(event);
        handleClick(event);
      },
    },
  ]);

  const getStylesForType = () => {
    const getPrimaryStyles = () => ({
      // display: 'flex',
      backgroundColor: '#313131',
      borderRadius: '100px',
      boxSizing: 'border-box',
      color: '#ffffff',
      padding: '8px 24px',
      fontSize: '16px',
      height: '44px',
      alignItems: 'center',
      textAlign: 'center',
      lineHeight: '28px',
      fontWeight: '500',
      filter: 'none',
    });

    switch (type) {
      case 'primary':
        return getPrimaryStyles();
      case 'primaryRed':
        return { ...getPrimaryStyles(), ...{ backgroundColor: '#E7554B' } };
      case 'secondary':
        return {
          // display: 'flex',
          backgroundColor: '#ffffff',
          border: '1px solid #313131',
          borderRadius: '100px',
          color: '#313131',
          padding: '8px 24px',
          fontSize: '16px',
          height: '44px',
          alignItems: 'center',
          textAlign: 'center',
          lineHeight: '28px',
          fontWeight: '500',
        };
      case 'tertiary':
        return {
          backgroundColor: '#ffffff',
          border: '1px solid #AEAEAE',
          borderRadius: '100px',
          color: '#313131',
          padding: '8px 24px',
          fontSize: '16px',
          height: '44px',
          alignItems: 'center',
          textAlign: 'center',
          lineHeight: '28px',
          fontWeight: '500',
        };
      case 'standard':
        return {
          backgroundColor: '#ffffff',
          borderRadius: '6px',
          boxSizing: 'border-box',
          color: '#313131',
          padding: '16px 20px',
          fontSize: '16px',
          fontWeight: 'bold',
          WebkitBoxShadow: '0 4px 4px 0 rgba(208,208,208,0.25)',
          boxShadow: '0 4px 4px 0 rgba(208,208,208,0.25)',
        };
      case 'box':
        return {
          backgroundColor: '#F0F0F0',
          color: '#1A1919',
          width: '44px',
          height: '44px',
          padding: '10px',
          borderRadius: '8px',
        };
      case 'text':
        return {
          textDecoration: 'none',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'all 0.1s',
          whiteSpace: 'nowrap',
        };
      case 'table':
        return {
          boxSizing: 'border-box',
          textDecoration: 'underline',
          textDecorationColor: '#C0C0C0',
          textUnderlineOffset: '5px',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'all 0.1s',
          whiteSpace: 'nowrap',
        };
      case 'stripe':
        return {
          background: '#FFFFFF',
          border: '1px solid #6772E5' /* Stripe brand colour */,
          color: '#6772E5',
          padding: '12px 18px 11px 15px',
          fontWeight: 'bold',
          borderRadius: '4px',
          transitionDuration: '0.05s',
          position: 'absolute',
          top: '50%',
          MsTransform: 'translateY(-50%)',
          transform: 'translateY(-50%)',
        };
      case 'icon':
        return {
          width: '18px',
          height: '18px',
          paddingTop: '2px',
        };
      case 'custom':
        return {};
      default:
        return getPrimaryStyles();
    }
  };

  // TODO find a way to use JSS styles while retaining composition
  const buttonStyles = {
    container: {
      position: 'relative',
      ...style,
      // maxWidth: fullWidth ? "100%" : "auto",
      ...style?.container,
    },
    defaultButton: {
      ...style?.defaultButton,
    },
    loading: {
      cursor: 'default',
      ...style?.loading,
    },
    disabled: {
      cursor: 'default',
      backgroundColor: '#e8e8e8',
      color: '#aaaaaa',
      filter: 'none',
      ...style?.disabled,
    },
    button: {
      backgroundColor: color,
      width: fullWidth ? '100%' : 'auto',
      display: 'inline-block',
      boxSizing: 'border-box',
      textDecoration: 'none',
      cursor: 'pointer',
      userSelect: 'none',
      textAlign: 'center',
      transition: 'all 0.05s',
      whiteSpace: 'nowrap',
      filter: 'none',
      ...style?.defaultButton,
      ...getStylesForType(),
      ...style?.button,
    },
    buttonHover: {
      filter: 'brightness(95%)',
      ...style?.buttonHover,
    },
    buttonActive: {
      ...style?.buttonActive,
    },
    buttonFocus: {
      outline: '1px solid black',
      ...style?.buttonFocus,
    },
    buttonIcon: {
      width: '18px',
      height: '18px',
      paddingTop: '2px',
    },
    iconSpaceMargin: {
      width: '1px',
      marginLeft: '1px',
      marginRight: '1px',
      height: '18px',
      position: 'relative',
      marginTop: '2px',
    },
    iconSpaceMarginInner: {
      width: '1px',
      backgroundColor: '#6E6E6D',
      height: '100%',
      margin: '0',
      position: 'absolute',
      top: '50%',
      MsTransform: 'translateY(-50%)',
      transform: 'translateY(-50%)',
    },
    link: {
      userSelect: 'none',
      ...style?.link,
    },
    text: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: centered ? 'center' : 'flex-start',
      // justifyContent: 'center',
      color: 'inherit',
      textDecoration: underline ? 'underline' : 'inherit',
      // width: '100%',
      height: '100%',
      ...style?.text,
    },
  };

  // TODO prevent circular progress from resizing the button. Maybe make contents invisible and absolutely position the spinner

  return (
    <Box className={`button-container ${className}`} style={buttonStyles.container}>
      <ButtonContainer
        href={href}
        buttonStyles={buttonStyles}
        newTab={newTab}
        loading={loading}
        disabled={disabled}
      >
        <Box
          tabIndex={disabled ? -1 : 0}
          ref={ref}
          onClick={handleClick}
          onMouseOver={() => {
            setHovered(true);
            onHover();
          }}
          onMouseLeave={() => {
            setHovered(false);
            onLeave();
          }}
          // onFocus={(e) => {
          //   setFocused(true);
          // }}
          // onBlur={() => {
          //   setFocused(false);
          // }}
          style={{
            ...buttonStyles.defaultButton,
            ...buttonStyles.button,
            ...((hovered || forceHoverState) && !loading && !disabled
              ? buttonStyles.buttonHover
              : ''),
            ...(forceHoverState ? buttonStyles.buttonActive : ''),
            ...(focused ? buttonStyles.buttonFocus : ''),
            ...(loading ? buttonStyles.loading : ''),
            ...(disabled ? buttonStyles.disabled : ''),
          }}
          className={`button ${getClass()} ${disabled ? 'button-disabled' : ''}`}
        >
          {loading ? (
            <Box style={{ position: 'relative' }}>
              <Box style={{ position: 'absolute', width: '100%' }}>
                <CircularProgress size={26} color='inherit' />
              </Box>
              <Box className='button-text' style={{ ...buttonStyles?.text, opacity: '0' }}>
                {text} {children}
              </Box>
            </Box>
          ) : (
            <>
              <Box className='button-text' title={title} style={{ ...buttonStyles?.text }}>
                {text} {children}
              </Box>
            </>
          )}
        </Box>
      </ButtonContainer>
    </Box>
  );
};

Button.defaultProps = {
  className: '',
  style: null,
  onClick: (e) => e,
  onEnter: () => {},
  onHover: (e) => e,
  onLeave: (e) => e,
  disabled: false,
  type: 'primary',
  children: '',
  resetFocus: false,
  text: '',
  underline: false,
  href: null,
  newTab: true,
  color: null,
  fullWidthOnMobile: false,
  loading: false,
  fullWidth: false,
  centered: true,
  forceHoverState: false,
  title: null,
};

Button.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  type: PropTypes.string,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  disabled: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.shape({}),
  onClick: PropTypes.func,
  onEnter: PropTypes.func, // Functionality specific to pressing enter on the keyboard for this button
  resetFocus: PropTypes.bool, // Resets the focus to the top of the page, so it can be tabbed through again
  underline: PropTypes.bool, // Whether the text in the button should be underline. Defaults to false
  href: PropTypes.string, // If provided, will function as a html link instead of using OnClick
  color: PropTypes.string, // Changes the background colour of the button
  newTab: PropTypes.bool, // Whether to open the href in a new tab or the same one.
  fullWidthOnMobile: PropTypes.bool, // If on mobile size, make this button take the full width
  loading: PropTypes.bool,
  fullWidth: PropTypes.bool,
  centered: PropTypes.bool,
  forceHoverState: PropTypes.bool, // Whether to show this button as hovered even if it isn't (e.g. if it opened some sub-menu)
  title: PropTypes.string, // Show title info on mouse hover
  onHover: PropTypes.func, // Callback when button is hovered,
  onLeave: PropTypes.func, // Callback when button is no longer hovered,
};
